/**
 * One role in depth (S-04).
 *
 * Reached by clicking a role on Team, which is where every role is listed with
 * its state, its task and its live output. This screen is what that card cannot
 * hold: the full detail list and the output at full height.
 */

import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router';

import { agentCardDetails, displaySpec } from '../../logic/display-matrix.js';
import { useStoreState } from '../store-context.js';
import { LiveOutput } from '../components/live-output.js';
import { Chip, QueryPhase } from '../components/state.js';
import { Button } from '@primer/react';

export function AgentScreen(): ReactNode {
  const { id } = useParams();
  const scene = useStoreState((state) => state.scene);
  const transcripts = useStoreState((state) => state.transcripts);

  if (scene.status !== 'ready') {
    return (
      <QueryPhase
        isPending={scene.status === 'loading'}
        error={scene.status === 'error' ? scene.message : null}
        source="team projection"
        rows={4}
      />
    );
  }

  const agent = scene.value.agents.find((entry) => entry.roleInstanceId === id) ?? null;
  if (agent === null) {
    return (
      <div className="stack">
        <p className="placeholder-inline">This role is not in the current team.</p>
        <p>
          <Button as={Link} to="/overview">
            Back to the team
          </Button>
        </p>
      </div>
    );
  }

  const spec = displaySpec(agent.state);
  const transcript = transcripts.get(agent.roleInstanceId) ?? null;
  const working = agent.state === 'working' || agent.state === 'delivering';

  return (
    <div className="stack">
      <p>
        <Button as={Link} to="/overview">
          Back to the team
        </Button>
      </p>

      <section className="panel" aria-label={agent.displayName}>
        <h2>{agent.displayName}</h2>
        <p className="panel-lede">
          <Chip tone={spec.tone} text={spec.badgeText} /> {agent.stateExplanation}
        </p>
        <dl className="detail-list">
          {/* "Why" is the lede above; a screen that says it twice reads as a bug. */}
          {agentCardDetails(agent)
            .filter((line) => line.label !== 'Why')
            .map((line) => (
              <div key={line.label} style={{ display: 'contents' }}>
                <dt>{line.label}</dt>
                <dd>{line.value}</dd>
              </div>
            ))}
        </dl>
      </section>

      {transcript === null && !working ? null : (
        <section className="panel panel-output" aria-label="Latest output">
          <h2>Latest output</h2>
          <LiveOutput transcript={transcript} working={working} />
        </section>
      )}
    </div>
  );
}
